/**
 * Tenant URL Resolution
 *
 * Resolves the active tenant from the URL path prefix /tenant/:code/
 * or falls back to the VITE_TENANT_SLUG environment variable.
 *
 * URL patterns:
 *   https://example.com/tenant/nbc/...  → tenant "nbc"
 *   https://example.com/...             → default from env
 *
 * Used by all frontend apps (pickem, squares, daily-reveal).
 */

const TENANT_PATH_REGEX = /^\/tenant\/([a-zA-Z0-9_-]+)/;
const GAME_ID_PATH_REGEX = /^(?:\/tenant\/[a-zA-Z0-9_-]+)?\/game\/([a-zA-Z0-9-]+)/;

/** Resolve tenant code from URL path or environment fallback */
export function resolveTenantFromUrl(): string {
  const match = window.location.pathname.match(TENANT_PATH_REGEX);
  if (match) {
    return match[1].toLowerCase();
  }
  return import.meta.env?.VITE_TENANT_SLUG || 'dk';
}

/** Get the tenant base path prefix (e.g. "/tenant/nbc" or "") */
export function getTenantBasePath(): string {
  const match = window.location.pathname.match(TENANT_PATH_REGEX);
  if (match) {
    return `/tenant/${match[1].toLowerCase()}`;
  }
  return '';
}

/** Check if the current URL uses tenant path routing */
export function isUrlTenantMode(): boolean {
  return TENANT_PATH_REGEX.test(window.location.pathname);
}

/**
 * Resolve the game id from the URL path, if present.
 * Matches /game/:id and /tenant/:code/game/:id.
 * Returns null when no game segment is present.
 */
export function resolveGameIdFromUrl(): string | null {
  const match = window.location.pathname.match(GAME_ID_PATH_REGEX);
  return match ? match[1] : null;
}
